import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Paintbrush, Download, Save, Plus, X, Eye, Building2, Target, Palette } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhiteLabelConfig = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState({
    company_name: '',
    sector: '',
    target_keywords: [],
    primary_color: '#0055FF',
    secondary_color: '#00E5FF',
    accent_color: '#10B981',
    logo_text: ''
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [configured, setConfigured] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/whitelabel/config`, authHeaders);
      setConfig({
        company_name: res.data.company_name || '',
        sector: res.data.sector || '',
        target_keywords: res.data.target_keywords || [],
        primary_color: res.data.primary_color || '#0055FF',
        secondary_color: res.data.secondary_color || '#00E5FF',
        accent_color: res.data.accent_color || '#10B981',
        logo_text: res.data.logo_text || ''
      });
      setConfigured(res.data.configured || false);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/whitelabel/config`, config, authHeaders);
      setSaved(true);
      setConfigured(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Failed to save config:', err);
    } finally {
      setSaving(false);
    }
  };

  const downloadPlugin = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/plugin/download`, {
        ...authHeaders,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const slug = config.company_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') || 'seo-aeo-orchestra';
      a.download = `${slug}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      if (err.response?.status === 403) {
        alert('Licenza attiva richiesta per scaricare il plugin.');
      }
    } finally {
      setDownloading(false);
    }
  };

  const addKeyword = () => {
    const kw = newKeyword.trim();
    if (kw && !config.target_keywords.includes(kw)) {
      setConfig(prev => ({ ...prev, target_keywords: [...prev.target_keywords, kw] }));
      setNewKeyword('');
    }
  };

  const removeKeyword = (kw) => {
    setConfig(prev => ({ ...prev, target_keywords: prev.target_keywords.filter(k => k !== kw) }));
  };

  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-8" data-testid="whitelabel-config">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: config.primary_color + '20' }}>
            <Paintbrush className="w-5 h-5" style={{ color: config.primary_color }} />
          </div>
          <div>
            <h2 className="font-['Outfit'] text-lg font-medium">Configurazione White-Label</h2>
            <p className="text-xs text-[#A1A1AA]">Personalizza il plugin WP con il tuo branding</p>
          </div>
        </div>
        {configured && (
          <span className="px-3 py-1 rounded-full text-xs bg-[#10B981]/20 text-[#10B981]">Configurato</span>
        )}
      </div>

      {/* Live Preview Card */}
      <div className="bg-[#0a0a0a] rounded-2xl p-6 border border-white/5" data-testid="whitelabel-preview">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-4 h-4 text-[#A1A1AA]" />
          <span className="text-xs text-[#A1A1AA] uppercase tracking-wider">Anteprima Plugin WP</span>
        </div>
        {/* Simulated WP Admin Header */}
        <div className="rounded-xl overflow-hidden border border-white/10">
          <div className="p-5 rounded-t-xl" style={{ background: `linear-gradient(135deg, ${config.primary_color}, ${config.secondary_color})` }}>
            <h3 className="text-white font-bold text-lg">{config.company_name || 'Il tuo Brand'}</h3>
            <p className="text-white/80 text-sm">Plugin WordPress con agenti AI per il SEO</p>
          </div>
          <div className="bg-white p-4 rounded-b-xl">
            <div className="grid grid-cols-4 gap-3">
              {['Analisi SEO', 'Meta Tags', 'Contenuti AI', 'Local SEO'].map((tool, i) => (
                <div key={i} className="text-center p-3 rounded-lg" style={{ backgroundColor: (i === 0 ? config.primary_color : i === 1 ? config.accent_color : i === 2 ? '#F59E0B' : '#EF4444') + '15' }}>
                  <div className="w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: i === 0 ? config.primary_color : i === 1 ? config.accent_color : i === 2 ? '#F59E0B' : '#EF4444' }}>
                    <span className="text-white text-xs font-bold">{tool.charAt(0)}</span>
                  </div>
                  <span className="text-[10px] text-gray-600 font-medium">{tool}</span>
                </div>
              ))}
            </div>
            {config.sector && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <span className="text-[10px] text-gray-400">Settore:</span>
                <span className="text-[10px] text-gray-600 ml-1">{config.sector}</span>
              </div>
            )}
            {config.target_keywords.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {config.target_keywords.slice(0, 5).map((kw, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: config.primary_color }}>
                    {kw}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Config Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm font-medium">Informazioni Azienda</span>
          </div>
          
          <div>
            <label className="text-xs text-[#A1A1AA] block mb-1.5">Nome Azienda / Brand *</label>
            <Input
              value={config.company_name}
              onChange={(e) => updateField('company_name', e.target.value)}
              placeholder="Es: TechSEO Pro"
              className="bg-[#0a0a0a] border-white/10 text-white"
              data-testid="whitelabel-company-name"
            />
          </div>
          
          <div>
            <label className="text-xs text-[#A1A1AA] block mb-1.5">Settore</label>
            <select
              value={config.sector}
              onChange={(e) => updateField('sector', e.target.value)}
              className="w-full bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              data-testid="whitelabel-sector"
            >
              <option value="">Seleziona settore...</option>
              <option value="ecommerce">E-commerce</option>
              <option value="saas">SaaS / Software</option>
              <option value="agency">Agenzia Web / Marketing</option>
              <option value="healthcare">Salute / Benessere</option>
              <option value="real_estate">Immobiliare</option>
              <option value="restaurant">Ristorazione / Food</option>
              <option value="education">Educazione / Formazione</option>
              <option value="finance">Finanza / Assicurazioni</option>
              <option value="legal">Legale / Studio Legale</option>
              <option value="travel">Turismo / Viaggi</option>
              <option value="automotive">Automotive</option>
              <option value="other">Altro</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-[#A1A1AA] block mb-1.5">Testo Logo (opzionale)</label>
            <Input
              value={config.logo_text}
              onChange={(e) => updateField('logo_text', e.target.value)}
              placeholder="Es: TechSEO"
              className="bg-[#0a0a0a] border-white/10 text-white"
              data-testid="whitelabel-logo-text"
            />
          </div>
        </div>

        {/* Colors */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Palette className="w-4 h-4 text-[#A1A1AA]" />
            <span className="text-sm font-medium">Colori Branding</span>
          </div>
          
          {[
            { label: 'Colore Primario', field: 'primary_color' },
            { label: 'Colore Secondario', field: 'secondary_color' },
            { label: 'Colore Accento', field: 'accent_color' }
          ].map(({ label, field }) => (
            <div key={field} className="flex items-center gap-3">
              <input
                type="color"
                value={config[field]}
                onChange={(e) => updateField(field, e.target.value)}
                className="w-10 h-10 rounded-lg border-0 cursor-pointer bg-transparent"
                data-testid={`whitelabel-${field}`}
              />
              <div className="flex-1">
                <label className="text-xs text-[#A1A1AA] block">{label}</label>
                <Input
                  value={config[field]}
                  onChange={(e) => updateField(field, e.target.value)}
                  className="bg-[#0a0a0a] border-white/10 text-white text-xs h-8 font-mono"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Keywords */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[#A1A1AA]" />
          <span className="text-sm font-medium">Keyword Target</span>
        </div>
        <div className="flex gap-2 mb-3">
          <Input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
            placeholder="Aggiungi keyword..."
            className="bg-[#0a0a0a] border-white/10 text-white flex-1"
            data-testid="whitelabel-keyword-input"
          />
          <Button
            onClick={addKeyword}
            variant="outline"
            className="border-white/10 hover:bg-white/5"
            data-testid="whitelabel-add-keyword-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {config.target_keywords.map((kw, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm"
              style={{ backgroundColor: config.primary_color + '20', color: config.primary_color }}
            >
              {kw}
              <button onClick={() => removeKeyword(kw)} className="hover:opacity-70" data-testid={`remove-keyword-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          {config.target_keywords.length === 0 && (
            <span className="text-xs text-[#A1A1AA]">Nessuna keyword aggiunta</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-white/10">
        <Button
          onClick={saveConfig}
          disabled={saving || !config.company_name}
          className="text-white rounded-xl flex-1"
          style={{ backgroundColor: config.primary_color }}
          data-testid="whitelabel-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio...' : saved ? 'Salvato!' : 'Salva Configurazione'}
        </Button>
        <Button
          onClick={downloadPlugin}
          disabled={downloading || !configured}
          className="bg-[#10B981] hover:bg-[#0D9668] text-white rounded-xl flex-1"
          data-testid="whitelabel-download-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? 'Preparazione...' : 'Scarica Plugin Personalizzato'}
        </Button>
      </div>
      {!configured && (
        <p className="text-xs text-[#A1A1AA] text-center">Salva la configurazione per abilitare il download del plugin personalizzato</p>
      )}
    </div>
  );
};

export default WhiteLabelConfig;
